import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {isStructuredTable} from 'notebook/TypePredicates';
import {
  AnyStructuredTable,
  isNotePartWithItem,
  NotePart,
  NotePartItem,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';

import {NoteLinkedGroup} from '../NoteLinkedGroupConstants';

export function reduceGroupUpdateNotePart<T = unknown>(
  group: NoteLinkedGroup<T>,
  updatedNotePart: NotePart
): NoteLinkedGroup<T> {
  // eslint-disable-next-line benchlint/no-function-ref-in-array-method -- _.matcher intended for this purpose
  const idx = group.containedNoteParts.findIndex(_.matcher({cid: updatedNotePart.cid}));
  assert(idx !== -1, 'Unable to perform NoteLinkedGroup update, matching notePart not found', {
    provenanceUuid: group.provenanceUuid,
    groupKind: group.kind,
    updatedNotePartCid: updatedNotePart.cid,
    notePartCids: group.containedNoteParts.map((notePart) => notePart.cid),
  });
  return reactUpdate(group, {containedNoteParts: {[idx]: {$set: updatedNotePart}}});
}

export function reduceGroupUpdateNotePartItem<T = unknown>(
  group: NoteLinkedGroup<T>,
  notePart: NotePart,
  updatedItem: NotePartItem
): NoteLinkedGroup<T> {
  return reduceGroupUpdateNotePart(group, {...notePart, item: updatedItem});
}

/**
 * Find the notePart with matching cid contained within the NoteLinkedGroup.
 */
export function findMatchingNotePartInGroup(
  group: NoteLinkedGroup<unknown>,
  notePart: NotePart
): NotePart | null {
  return group.containedNoteParts.find(_.matcher({cid: notePart.cid})) ?? null;
}

export function findNotePartForStructuredTable<T extends AnyStructuredTable>(
  group: NoteLinkedGroup<unknown>,
  structuredTableKind: StructuredTableKind
): NotePartWithItem<T> | null {
  function isNotePartWithMatchingStructuredTable(notePart: NotePart): notePart is NotePartWithItem<T> {
    return (
      isNotePartWithItem(notePart) &&
      isStructuredTable(notePart.item) &&
      notePart.item.kind === structuredTableKind
    );
  }
  return group.containedNoteParts.find<NotePartWithItem<T>>(isNotePartWithMatchingStructuredTable) ?? null;
}
